import { useInfiniteQuery } from '@tanstack/react-query';
import ObjectiveFeedService from '@apis/ObjectiveFeedService';
import type { ObjectiveFeedResponse, ObjectiveFeedGroup } from '@customTypes/objectiveFeed';

export const objectiveFeedQueryKeys = {
  all: ['objectiveFeed'] as const,
  list: () => ['objectiveFeed', 'list'] as const,
};

/**
 * Hook for fetching the objective-grouped feed with cursor-based pagination.
 */
export function useObjectiveFeed(options?: { enabled?: boolean }) {
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useInfiniteQuery<ObjectiveFeedResponse>({
    queryKey: objectiveFeedQueryKeys.list(),
    queryFn: async ({ pageParam }) => {
      return ObjectiveFeedService.getObjectiveFeed(
        undefined,
        pageParam as string | undefined,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    initialPageParam: undefined as string | undefined,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    enabled: options?.enabled !== false,
  });

  const objectiveGroups: ObjectiveFeedGroup[] =
    data?.pages.flatMap((page) => page.objectives) ?? [];

  return {
    objectiveGroups,
    isLoading,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    error,
    refetch,
  };
}
